import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { Plus, X, Warehouse as WarehouseIcon, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CountrySelect, ProvinceSelect } from "@/components/GeoSelect";

const EMPTY = { nombre: "", direccion: "", ciudad: "", provincia: "", pais: "", oficina_asignada: "", capacidad_m3: 0, activo: true };

export default function Warehouses() {
  const { api } = useAuth();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/warehouses").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/oficinas").then((r) => setOficinas(r.data.oficinas || [])).catch(() => {});
    api.get("/countries").then((r) => setCountries(r.data.filter((c) => c.activo))).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.patch(`/warehouses/${editingId}`, form);
      else await api.post("/warehouses", form);
      toast.success(t("saved"));
      setShowForm(false); setEditingId(null); setForm(EMPTY);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/warehouses/${id}`);
    toast.success(t("deleted"));
    load();
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100";
  const lbl = "text-[11px] font-semibold text-slate-600 uppercase mb-1 block";

  return (
    <div className="space-y-4" data-testid="warehouses-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <WarehouseIcon className="w-5 h-5 text-red-600" /> {t("warehouses")}
          </h2>
          <p className="text-sm text-slate-500">{rows.length} {t("warehouses").toLowerCase()}</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditingId(null); setShowForm(true); }} data-testid="new-warehouse-btn" className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("new_warehouse")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("name")}</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("address")}</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("city")}</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("country")}</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">{t("assigned_branch")}</th>
              <th className="p-3 text-start text-[11px] uppercase tracking-wider font-semibold">Cap. m³</th>
              <th className="p-3 text-end text-[11px] uppercase tracking-wider font-semibold">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`warehouse-${w.id}`}>
                <td className="p-3 font-semibold text-slate-900">{w.nombre}</td>
                <td className="p-3 text-slate-600">{w.direccion || "—"}</td>
                <td className="p-3 text-slate-600">{w.ciudad || "—"}</td>
                <td className="p-3 text-slate-600">{w.pais || "—"}</td>
                <td className="p-3">
                  {w.oficina_asignada ? (
                    <span className="text-[10px] bg-fuchsia-100 text-fuchsia-700 px-2 py-0.5 rounded font-semibold">{w.oficina_asignada}</span>
                  ) : "—"}
                </td>
                <td className="p-3 tabular-nums text-slate-600">{w.capacidad_m3 || "—"}</td>
                <td className="p-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => { setForm({ ...EMPTY, ...w }); setEditingId(w.id); setShowForm(true); }} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`edit-warehouse-${w.id}`}>
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(w.id)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-slate-400">
                  <WarehouseIcon className="w-10 h-10 mx-auto opacity-30 mb-2" />
                  {t("no_warehouses")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="warehouse-modal">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-semibold text-lg">{editingId ? t("edit") : t("new_warehouse")}</h3>
              <button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={lbl}>{t("name")} *</label>
                <input required data-testid="wf-name" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className={inp} />
              </div>
              <div className="sm:col-span-2">
                <label className={lbl}>{t("address")}</label>
                <input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("city")}</label>
                <input value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("province")}</label>
                <ProvinceSelect countries={countries} country={form.pais} value={form.provincia} onChange={(v) => setForm({ ...form, provincia: v })} className={inp} testid="wf-province" />
              </div>
              <div>
                <label className={lbl}>{t("country")}</label>
                <CountrySelect countries={countries} value={form.pais} onChange={(v) => setForm({ ...form, pais: v, provincia: "" })} className={inp} testid="wf-country" />
              </div>
              <div>
                <label className={lbl}>{t("assigned_branch")}</label>
                <select data-testid="wf-branch" value={form.oficina_asignada} onChange={(e) => setForm({ ...form, oficina_asignada: e.target.value })} className={inp}>
                  <option value="">—</option>
                  {oficinas.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Capacidad m³</label>
                <input type="number" step="0.1" value={form.capacidad_m3} onChange={(e) => setForm({ ...form, capacidad_m3: +e.target.value })} className={inp} />
              </div>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
                {t("active")}
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 py-2.5 rounded-lg font-semibold text-sm">{t("cancel")}</button>
              <button type="submit" data-testid="wf-submit" className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-semibold text-sm">{t("save")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
